import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Image as CImage,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { MdRotateRight } from 'react-icons/md'

import Resizer from 'react-image-file-resizer'

const alwaysPortrait = (file) =>
  new Promise((resolve, reject) => {
    const portraitImage = new Image()
    const objectUrl = URL.createObjectURL(file)
    portraitImage.src = objectUrl
    portraitImage.onload = () => {
      URL.revokeObjectURL(objectUrl)
      if (portraitImage.width > portraitImage.height) {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = portraitImage.height
        canvas.height = portraitImage.width
        ctx.translate(portraitImage.height / 2, portraitImage.width / 2)
        ctx.rotate((270 * Math.PI) / 180)
        ctx.drawImage(
          portraitImage,
          -portraitImage.width / 2,
          -portraitImage.height / 2,
          portraitImage.width,
          portraitImage.height
        )
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo procesar la imagen'))
              return
            }
            resolve(blob)
          },
          'image/jpeg',
          1
        )
      } else {
        resolve(file)
      }
    }
    portraitImage.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('No se pudo cargar la imagen'))
    }
  })

const blobToFile = (theBlob, fileName) => {
  if (theBlob instanceof File) return theBlob
  return new File([theBlob], fileName, {
    type: theBlob.type || 'image/jpeg',
    lastModified: Date.now()
  })
}

const resizeFile = (file, grades) =>
  new Promise((resolve, reject) => {
    Resizer.imageFileResizer(
      file,
      650,
      650,
      'JPEG',
      100,
      grades,
      (uri) => {
        resolve(uri)
      },
      'file',
      650,
      650
    )
  })

const PreviewImage = ({ file = null, callBackFile, isOpen, onClose }) => {
  const [newImageUri, setWewImageUri] = useState(null)
  const [multipleImages, setMultipleImages] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [grades, setGrades] = useState(360)
  const [isProcessing, setIsProcessing] = useState(false)

  const changeGrades = async () => {
    if (!newImageUri) return
    const nextGrade = grades + 90
    const newValue = nextGrade > 360 ? 90 : nextGrade
    const reformatedImage = await resizeFile(newImageUri, newValue)

    if (multipleImages.length) {
      const updated = [...multipleImages]
      updated[selectedIndex] = reformatedImage
      setMultipleImages(updated)
    }
    setWewImageUri(reformatedImage)
    setGrades(newValue)
  }

  const sendFile = () => {
    if (callBackFile) {
      if (multipleImages.length) {
        callBackFile(multipleImages)
      } else if (newImageUri) {
        callBackFile(newImageUri)
      }
    }
    onClose()
  }

  const moveImage = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= multipleImages.length) return

    const reordered = [...multipleImages]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(newIndex, 0, moved)
    setMultipleImages(reordered)

    setSelectedIndex((prev) => {
      if (prev === index) return newIndex
      if (index < newIndex) {
        if (prev > index && prev <= newIndex) return prev - 1
      } else if (index > newIndex) {
        if (prev >= newIndex && prev < index) return prev + 1
      }
      return prev
    })
  }

  useEffect(() => {
    const processImages = async () => {
      if (!file) {
        setMultipleImages([])
        setWewImageUri(null)
        setSelectedIndex(0)
        setGrades(360)
        setIsProcessing(false)
        return
      }

      setIsProcessing(true)

      try {
        if (!Array.isArray(file)) {
          const rotatedImage = await alwaysPortrait(file)
          const formatedfile = blobToFile(rotatedImage, file.name)
          const uri = await resizeFile(formatedfile, 0)
          setMultipleImages([])
          setWewImageUri(uri)
          setSelectedIndex(0)
          return
        }

        const allImages = []
        for (let i = 0; i < file.length; i++) {
          const fileItem = file[i]
          const rotatedImage = await alwaysPortrait(fileItem)
          const formatedfile = blobToFile(
            rotatedImage,
            fileItem.name || `imagen-${i + 1}.jpeg`
          )
          const uri = await resizeFile(formatedfile, 0)
          allImages.push(uri)
        }
        setMultipleImages(allImages)
        setWewImageUri(allImages[0] || null)
        setSelectedIndex(0)
      } catch (error) {
        console.error(error)
        alert('No se pudieron procesar una o más imágenes. Intenta de nuevo.')
        onClose()
      } finally {
        setIsProcessing(false)
      }
    }

    processImages()
  }, [file])

  const selectPhoto = (index) => {
    setSelectedIndex(index)
    setWewImageUri(multipleImages[index])
    setGrades(360)
  }

  const isMultiple = multipleImages.length > 1
  const selectedCount = Array.isArray(file) ? file.length : file ? 1 : 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="lg">
        <ModalHeader>
          {isMultiple || selectedCount > 1
            ? 'Editar orientación y orden de imágenes'
            : 'Editar orientación de imagen'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isProcessing ? (
            <Flex justify="center" align="center" py={10} direction="column" gap={3}>
              <Spinner color="#59BABA" size="lg" />
              <Text fontSize="sm" color="#666587">
                Procesando {selectedCount} imagen{selectedCount !== 1 ? 'es' : ''}…
              </Text>
            </Flex>
          ) : (
            <>
              <Flex
                justify="center"
                alignItems="center"
                gap={2}
                direction="column"
                border="1px solid #59BABA"
                borderRadius={8}
                w="100%"
                py={4}
                minH="200px"
              >
                {newImageUri ? (
                  <Box>
                    <CImage
                      src={URL.createObjectURL(newImageUri)}
                      maxH={300}
                      maxW={300}
                      alt="preview"
                      objectFit="contain"
                    />
                  </Box>
                ) : (
                  <Text fontSize="sm" color="#666587">
                    Selecciona una miniatura para previsualizar
                  </Text>
                )}
                <Box>
                  <Button
                    bg={'yellow.500'}
                    onClick={changeGrades}
                    rightIcon={<MdRotateRight />}
                    isDisabled={!newImageUri}
                  >
                    Rotar
                  </Button>
                </Box>
              </Flex>

              {isMultiple && (
                <Text fontSize="xs" color="#666587" mt={3}>
                  Usa las flechas ▲ ▼ en cada miniatura para definir el orden (1, 2,
                  3…) antes de confirmar.
                </Text>
              )}

              {multipleImages.length > 0 && (
                <HStack
                  mt={3}
                  spacing={4}
                  sx={{ overflowX: 'auto' }}
                  align="flex-start"
                  flexWrap="wrap"
                >
                  {multipleImages.map((fileItem, idx) => (
                    <VStack key={`preview-${idx}`} spacing={1} minW="100px">
                      {isMultiple && (
                        <HStack spacing={1}>
                          <Text fontSize="sm" fontWeight="bold" color="#36355F">
                            {idx + 1}
                          </Text>
                          <IconButton
                            aria-label="Subir imagen"
                            icon={<ChevronUpIcon />}
                            size="xs"
                            isDisabled={idx === 0}
                            onClick={() => moveImage(idx, 'up')}
                          />
                          <IconButton
                            aria-label="Bajar imagen"
                            icon={<ChevronDownIcon />}
                            size="xs"
                            isDisabled={idx === multipleImages.length - 1}
                            onClick={() => moveImage(idx, 'down')}
                          />
                        </HStack>
                      )}
                      <CImage
                        src={URL.createObjectURL(fileItem)}
                        boxSize="100px"
                        objectFit="contain"
                        border={
                          selectedIndex === idx
                            ? '2px solid #59BABA'
                            : '1px solid #59BABA'
                        }
                        borderRadius={8}
                        onClick={() => selectPhoto(idx)}
                        cursor="pointer"
                      />
                    </VStack>
                  ))}
                </HStack>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={isProcessing}>
            Cancelar
          </Button>
          <Button
            bg={'teal.500'}
            color={'white'}
            onClick={sendFile}
            isDisabled={isProcessing || !newImageUri}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PreviewImage
